import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ChartComponent } from 'ng-apexcharts';
import { Subscription } from 'rxjs';
import { ChartOptions } from 'libs/portal/src/models/ChartOptions';
import { MemoryService } from 'libs/portal/src/services/memory/memory.service';
import { NavigationService } from 'libs/portal/src/services/navigation/navigation.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';

@Component({
  selector: 'app-data-bar-chart',
  templateUrl: './data-bar-chart.component.html',
  styleUrls: ['./data-bar-chart.component.scss'],
})
export class DataBarChartComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('chart') chart: ChartComponent;
  @Input() data: any[];
  @Input() month: Date;
  @Input() pagination = false;
  @Input() total: number;
  @Input() currentPage = 1;
  @Input() titleText: string;
  @Input() applyBoxStyling = true;
  @Input() enableDevicesToggle = false;
  @Input() showLegend = true;
  @Input() customLegendItems?: string[] = [];
  @Input() customLegendColors?: string[] | null = null;
  @Input() deviceToggleValue = 'All';
  @Input() enableRefresher = false;
  @Output() paginate: EventEmitter<any> = new EventEmitter<any>();
  @Output() selected: EventEmitter<number> = new EventEmitter<number>();
  @Output() deviceSelectionToggled = new EventEmitter<boolean>();
  @Input() refreshIconToolTip = 'Refresh';
  public chartOptions: Partial<ChartOptions>;

  memorySub: Subscription;

  graphLoaded = false;
  sidenavObservable: Subscription;
  private unit = 'MB';

  constructor(private cd: ChangeDetectorRef, private tools: ToolsService, private navigationService: NavigationService, private memoryService: MemoryService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['total']) {
      this.total = changes['total'].currentValue;
    }

    if (changes['data']) {
      this.data = changes['data'].currentValue;
      this.buildChart();
    }

    if (changes['currentPage']) {
      this.currentPage = changes['currentPage'].currentValue;
    }

    if (changes['titleText']) {
      this.titleText = changes['titleText'].currentValue;
      this.buildChart();
    }
  }

  ngOnInit() {
    this.initUnitChangeHandler();
    this.buildChart();
    this.cd.detectChanges();
    this.sidenavObservable = this.navigationService.GetSideNav(() => {
      if (this.graphLoaded != false) {
        const temp = this.graphLoaded;
        this.graphLoaded = false;
        setTimeout(() => {
          this.graphLoaded = temp;
        });
      }
    });
  }

  ngOnDestroy(): void {
      this.memorySub.unsubscribe();
  }

  initUnitChangeHandler(): void {
    this.unit = this.memoryService.getLocalPreferences().getValue().preferedGraphUnit;
    this.memorySub = this.memoryService.getLocalPreferences().subscribe((prefs) => {
      if (this.unit != prefs.preferedGraphUnit) {
        this.unit = prefs.preferedGraphUnit;
        this.buildChart();
      }
    });
  }

  deviceToggleChanged(): void {
    this.deviceSelectionToggled.emit(this.deviceToggleValue == 'All' ? true : false);
  }

  buildChart() {
    const seriesData: any = [];
    const distributedColours = this.data != null && this.data[0]?.ExceededUsage != null ? true : false;
    let colors: any = [];

    if (this.data != null && this.data[0]?.InBundleAmount != null) {
      seriesData.push({
        name: `Data Bundle (${this.unit})`,
        data:
          this.data != null
            ? this.unit == 'GB'
              ? this.data.map((device) => this.tools.ConvertDataFromBytes(device.InBundleAmount, this.unit).toFixed(2))
              : this.data.map((device) => this.tools.ConvertDataFromBytes(device.InBundleAmount, this.unit).toFixed(1))
            : [],
      });
      colors.push('#838383');
    }

    if (this.data != null && this.data[0]?.TotalUsage != null) {
      seriesData.push({
        name: `Data Used (${this.unit})`,
        data:
          this.data != null
            ? this.unit == 'GB'
              ? this.data.map((device) => this.tools.ConvertDataFromBytes(device.TotalUsage, this.unit).toFixed(2))
              : this.data.map((device) => this.tools.ConvertDataFromBytes(device.TotalUsage, this.unit).toFixed(1))
            : [],
      });
      colors.push('#C93F55');
    }

    if (distributedColours) {
      colors = [];
      this.data.forEach((elem) => {
        if (elem.ExceededUsage) {
          colors.push('#C93F55');
        } else if (elem.WillExceedBundle) {
          colors.push('#FFBE65');
        } else {
          colors.push('#3FC944');
        }
      });
    }

    let thisMonth = new Date();
    let endDate = this.month ? this.tools.GetEndOfMonthFromDate(this.month) : new Date();
    endDate = this.tools.GetStartOfMonth(endDate).toLocaleDateString() == this.tools.GetStartOfMonth(thisMonth).toLocaleDateString() ? thisMonth : endDate;
    this.chartOptions = {
      title: {
        // sv-SE because yyyy/MM/dd
        text:
          this.titleText != null
            ? this.titleText
            : `Bundle used in ${this.unit} (` +
              this.tools.GetStartOfMonth(this.month ?? new Date()).toLocaleDateString('sv-SE') +
              ' - ' +
              endDate.toLocaleDateString('sv-SE') +
              ')',
        align: 'left',
        style: {
          fontSize: '24px',
          color: '#08074B',
          fontWeight: '400',
        },
      },
      series: seriesData,
      grid: {
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      chart: {
        type: 'bar',
        height: 300,
        stacked: false,
        events: {
          selection: () => {},
          dataPointSelection: (_e, _c, config) => {
            this.notifySelected(config.dataPointIndex);
            // this.selected.emit(config.dataPointIndex);
          },
        },
        toolbar: {
          offsetX: -10,
          tools: {
            customIcons: this.enableRefresher
              ? [
                  {
                    icon: '<img src="/assets/icon/refresh.svg">',
                    index: -1,
                    title: this.refreshIconToolTip,
                    click: () => {
                      this.notifySelected(this.data.length - 1);
                      this.buildChart();
                    },
                  },
                ]
              : [],
          },
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      colors: this.customLegendColors ?? colors,
      plotOptions: {
        bar: {
          distributed: distributedColours,
          horizontal: false,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        style: {
          colors: ['#000000'],
        },
        offsetY: -20,
      },
      xaxis: {
        type: 'category',
        categories:
          this.data != null && this.data[0]?.Start != null ? this.data.map((ob) => ob.Start) : this.data[0]?.DeviceNum != null ? this.data.map((device) => device.DeviceNum) : [],
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      fill: {
        opacity: 1,
        colors: colors,
      },
      legend: {
        show: this.showLegend,
        customLegendItems: this.customLegendItems,
      },
    };

    this.graphLoaded = true;
  }

  onChangePage(page: number) {
    this.currentPage = page;
    this.paginate.emit(page);
  }

  notifySelected(num: number) {
    if (this.selected != null) {
      this.selected.emit(num);
    }
  }
}
